import { Budget, BudgetAnalysis, BudgetInsight, BudgetStats } from '@/types/budget';

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};
import { Transaction } from '@/types/transaction';
import { subMonths, isWithinInterval, eachDayOfInterval, isSameDay, format } from 'date-fns';

export const calculateBudgetStats = (budgets: Budget[], transactions: Transaction[]): BudgetStats => {
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - now.getDate() + 1; // +1 to include current day
  
  const totalBudgeted = budgets.reduce((sum, budget) => sum + budget.amount, 0);
  const totalSpent = budgets.reduce((sum, budget) => sum + budget.spent, 0);
  const remaining = totalBudgeted - totalSpent;
  const percentageSpent = totalBudgeted > 0 ? (totalSpent / totalBudgeted) * 100 : 0;
  
  // Calculate average daily spend and projections
  const daysElapsed = daysInMonth - daysRemaining;
  const averageDailySpend = daysElapsed > 0 ? totalSpent / daysElapsed : 0;
  const projectedEndSpend = averageDailySpend * daysInMonth;
  const projectedOverspend = projectedEndSpend > totalBudgeted ? projectedEndSpend - totalBudgeted : null;
  
  // Calculate category breakdown
  const categories = budgets.map(budget => {
    const categorySpent = transactions
      .filter(tx => tx.category?.id === budget.category.id)
      .reduce((sum, tx) => sum + tx.amount, 0);
      
    return {
      id: budget.category.id,
      name: budget.category.name,
      budgeted: budget.amount,
      spent: categorySpent,
      remaining: budget.amount - categorySpent,
      percentage: budget.amount > 0 ? (categorySpent / budget.amount) * 100 : 0
    };
  });

  return {
    totalBudgeted,
    totalSpent,
    remaining,
    daysRemaining,
    averageDailySpend,
    projectedEndSpend,
    projectedOverspend,
    categories
  };
};

export const analyzeSpending = (transactions: Transaction[], categoryId: string): BudgetAnalysis => {
  // Filter transactions for the specific category and last 3 months
  const now = new Date();
  const threeMonthsAgo = subMonths(now, 3);
  
  const relevantTransactions = transactions.filter(tx => 
    tx.category?.id === categoryId && 
    new Date(tx.date) >= threeMonthsAgo
  );

  // Calculate average spending
  const totalSpent = relevantTransactions.reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
  const averageSpend = totalSpent / 3; // Average over 3 months

  // Calculate spending trend (simple linear regression)
  const dailySpending: Record<string, number> = {};
  const days = eachDayOfInterval({ start: threeMonthsAgo, end: now });
  
  // Initialize all days with 0 spending
  days.forEach(day => {
    const dayKey = format(day, 'yyyy-MM-dd');
    dailySpending[dayKey] = 0;
  });

  // Add actual spending
  relevantTransactions.forEach(tx => {
    const dayKey = format(new Date(tx.date), 'yyyy-MM-dd');
    dailySpending[dayKey] = (dailySpending[dayKey] || 0) + Math.abs(tx.amount);
  });

  // Calculate trend (simplified)
  const dailyValues = Object.values(dailySpending);
  const sumX = dailyValues.reduce((sum, _, i) => sum + i, 0);
  const sumY = dailyValues.reduce((sum, y) => sum + y, 0);
  const sumXY = dailyValues.reduce((sum, y, i) => sum + (i * y), 0);
  const sumXX = dailyValues.reduce((sum, _, i) => sum + (i * i), 0);
  const n = dailyValues.length;
  
  // Slope (m) of the trend line
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const isTrendingUp = slope > 0;
  const trendStrength = Math.abs(slope) * 1000; // Arbitrary scaling factor

  // Generate insights
  const insights: string[] = [];
  
  if (trendStrength > 0.5) {
    insights.push(isTrendingUp 
      ? `Spending is trending up by ~${Math.round(trendStrength * 100) / 100}% per day`
      : `Spending is trending down by ~${Math.round(Math.abs(trendStrength) * 100) / 100}% per day`
    );
  } else {
    insights.push('Spending is relatively stable');
  }

  // Check for unusual spending patterns
  const avgDailySpend = totalSpent / 90; // 90 days
  const yesterdaySpend = dailyValues[dailyValues.length - 1] || 0;
  
  if (yesterdaySpend > avgDailySpend * 2) {
    insights.push(`Unusually high spending yesterday (${Math.round(yesterdaySpend / avgDailySpend)}x average)`);
  }

  // Generate a suggested budget (10% less than average, but not less than 70% of average)
  const suggestedBudget = Math.max(averageSpend * 0.9, averageSpend * 0.7);
  const confidence = relevantTransactions.length > 10 ? 'high' : relevantTransactions.length > 5 ? 'medium' : 'low';

  return {
    categoryId,
    currentSpend: totalSpent / 3, // Monthly average
    averageSpend,
    suggestedBudget,
    confidence,
    insights,
  };
};

export const generateBudgetInsights = (budget: Budget, transactions: Transaction[]): BudgetInsight[] => {
  const insights: BudgetInsight[] = [];
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - now.getDate();
  const dailyBudget = budget.amount / daysInMonth;
  const dailySpent = budget.spent / now.getDate();
  const projectedEndSpend = dailySpent * daysInMonth;
  
  // Projected over/under budget
  if (projectedEndSpend > budget.amount) {
    insights.push({
      id: `insight-${budget.id}-over-budget`,
      budgetId: budget.id,
      type: 'warning',
      message: `At current spending, you'll be ${formatCurrency(projectedEndSpend - budget.amount)} over budget this month`,
      createdAt: new Date().toISOString(),
      metadata: { 
        projectedOverspend: projectedEndSpend - budget.amount,
        actionType: 'adjust_budget'
      }
    });
  } else if (budget.spent / budget.amount > 0.8) {
    insights.push({
      id: `insight-${budget.id}-high-usage`,
      budgetId: budget.id,
      type: 'warning',
      message: `You've used ${Math.round((budget.spent / budget.amount) * 100)}% of your ${budget.category.name} budget`,
      createdAt: new Date().toISOString(),
      metadata: { percentageUsed: Math.round((budget.spent / budget.amount) * 100) }
    });
  }

  // Recent large purchases
  const recentLargePurchases = transactions
    .filter(tx => tx.category?.id === budget.category.id && tx.amount < 0)
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
    .slice(0, 3);

  if (recentLargePurchases.length > 0) {
    insights.push({
      id: `insight-${budget.id}-recent-purchase`,
      budgetId: budget.id,
      type: 'info',
      message: `Recent large purchase: ${formatCurrency(Math.abs(recentLargePurchases[0].amount))} on ${format(new Date(recentLargePurchases[0].date), 'MMM d')}`,
      createdAt: new Date().toISOString(),
      metadata: { 
        amount: Math.abs(recentLargePurchases[0].amount),
        date: recentLargePurchases[0].date
      }
    });
  }

  // Seasonal patterns (simplified)
  const month = now.getMonth();
  if (month === 11 && budget.category.name.toLowerCase().includes('gift')) {
    insights.push({
      id: `insight-${budget.id}-seasonal`,
      budgetId: budget.id,
      type: 'suggestion',
      message: 'Holiday season typically sees 30% higher spending in this category',
      createdAt: new Date().toISOString(),
      metadata: { season: 'holiday', impact: '30% increase' }
    });
  }

  // Sort by creation date (newest first)
  return insights.sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

export const calculateBudgetProgress = (budget: Budget) => {
  const percentage = Math.min(100, (budget.spent / budget.amount) * 100);
  const remaining = budget.amount - budget.spent;
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - now.getDate();
  const dailyBudget = budget.amount / daysInMonth;
  const dailySpent = budget.spent / (daysInMonth - daysRemaining);
  const projectedEndSpend = dailySpent * daysInMonth;
  
  return {
    percentage,
    remaining,
    daysRemaining,
    dailyBudget,
    dailySpent,
    projectedEndSpend,
    isOnTrack: projectedEndSpend <= budget.amount,
    // daysAheadOrBehind: Math.round((budget.spent - (dailyBudget * (daysInMonth - daysRemaining))) / dailyBudget)
  };
};
